import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { LcUtilsService } from './lc-utils.service';

export interface ILCFinalResult {
  candidateOrder: string;
  fullname: string;
  groupFullname: string;
  countNumber: number;
  votesEnd: number;
}

export interface IPartySeats {
  partyName: string;
  seats: number;
}

@Injectable({
  providedIn: 'root'
})
export class LcFinalResultsService {

  constructor(
    private http: HttpClient,
    private lcUtils: LcUtilsService
  ) {}

  /**
   * Load and parse the Order of Election Report CSV file
   * @param electionDate - The election date string (e.g., '2022-03-19')
   * @returns Observable of parsed final results
   */
  public loadOrderOfElectionReport$(electionDate: string): Observable<ILCFinalResult[]> {
    // Construct the CSV file path based on election date
    const csvPath = `assets/v31_13_OrderOfElectionReport.csv`;
    
    return this.http.get(csvPath, { responseType: 'text' }).pipe(
      map((csvText: string) => this.parseCSV(csvText)),
      catchError(error => {
        console.error('Error loading Order of Election Report CSV:', error);
        return of([]);
      })
    );
  }

  /**
   * Parse CSV text into structured data
   */
  private parseCSV(csvText: string): ILCFinalResult[] {
    const lines = csvText.split('\n');
    const results: ILCFinalResult[] = [];

    // Skip header row (index 0) and process data rows
    for (let i = 1; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) continue; // Skip empty lines

      const columns = this.parseCSVLine(line);
      if (columns.length >= 5) {
        results.push({
          candidateOrder: columns[0],
          fullname: columns[1],
          groupFullname: columns[2],
          countNumber: parseInt(columns[3], 10),
          votesEnd: parseInt(columns[4], 10)
        });
      }
    }

    return results;
  }

  /**
   * Parse a single CSV line using shared utility
   */
  private parseCSVLine(line: string): string[] {
    return this.lcUtils.parseCSVLine(line);
  }

  /**
   * Calculate seats won by each party from the final results
   * @param finalResults - Array of final election results
   * @returns Map of party name to number of seats won
   */
  public calculateSeatsWonByParty(finalResults: ILCFinalResult[]): Map<string, number> {
    const seatsMap = new Map<string, number>();

    // Only count candidates that were elected (candidateOrder contains "Elected")
    const electedCandidates = finalResults.filter(result => 
      result.candidateOrder && result.candidateOrder.toLowerCase().includes('elected')
    );

    // Count seats for each party
    electedCandidates.forEach(candidate => {
      const partyName = this.normalizePartyName(candidate.groupFullname);
      const currentSeats = seatsMap.get(partyName) || 0;
      seatsMap.set(partyName, currentSeats + 1);
    });

    return seatsMap;
  }

  /**
   * Normalize party name using shared utility
   */
  private normalizePartyName(groupFullname: string): string {
    return this.lcUtils.normalizePartyName(groupFullname);
  }

  /**
   * Get party seats as an array for easier processing
   */
  public getPartySeatsArray(finalResults: ILCFinalResult[]): IPartySeats[] {
    const seatsMap = this.calculateSeatsWonByParty(finalResults);
    const partySeats: IPartySeats[] = [];

    seatsMap.forEach((seats, partyName) => {
      partySeats.push({ partyName, seats });
    });

    // Sort by seats won (descending)
    return partySeats.sort((a, b) => b.seats - a.seats);
  }

  /**
   * Extract candidate information for elected candidates
   */
  public getElectedCandidates(finalResults: ILCFinalResult[]): ILCFinalResult[] {
    return finalResults.filter(result => 
      result.candidateOrder && result.candidateOrder.toLowerCase().includes('elected')
    ).sort((a, b) => {
      // Extract order number from candidateOrder (e.g., "1 - Elected" -> 1)
      const orderA = parseInt(a.candidateOrder.split('-')[0].trim(), 10);
      const orderB = parseInt(b.candidateOrder.split('-')[0].trim(), 10);
      return orderA - orderB;
    });
  }
}
